import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Search, X, Calendar, Zap, AlertCircle, CheckCircle, EyeOff, Eye } from 'lucide-react';
import { formatAmount } from '../../utils/formatters';
import { formatDateRelative } from '../../utils/dateUtils';
import BillIcon from './BillIcon';
import '../../styles/SearchInput.css';

/**
 * Bill search input with dropdown results
 * Shows matching bills in a dropdown, clicking opens the bill details
 * On mobile, collapses to a search icon that opens a search overlay
 * 
 * @param {Array} bills - Array of bill objects with dueInfo
 * @param {Array} categories - Array of category objects
 * @param {Array} paymentAccounts - Array of payment account objects for searching
 * @param {Array} members - Array of household members for assignee searching
 * @param {function} onBillSelect - Called when a bill is selected (bill) => void
 * @param {string} placeholder - Placeholder text
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Whether the input is disabled
 * @param {boolean} showInactive - Whether inactive bills are shown
 * @param {function} onShowInactiveChange - Called when show inactive toggle changes
 */
function BillSearchInput({ 
  bills = [],
  categories = [],
  paymentAccounts = [],
  members = [],
  onBillSelect,
  placeholder = 'Search bills...', 
  className = '',
  disabled = false,
  showInactive = false,
  onShowInactiveChange
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isMobileOverlayOpen, setIsMobileOverlayOpen] = useState(false);
  const inputRef = useRef(null);
  const mobileInputRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Get category info helper
  const getCategoryInfo = (categoryName) => {
    const category = categories.find(c => c.name === categoryName);
    return category ? { icon: category.icon } : { icon: '📋' };
  };

  // Get payment account name by ID
  const getPaymentAccountName = React.useCallback((accountId) => {
    if (!accountId) return '';
    const account = paymentAccounts.find(a => a.id === accountId);
    return account?.account_name || '';
  }, [paymentAccounts]);

  // Get assignee name by ID
  const getAssigneeName = React.useCallback((assigneeId) => {
    if (!assigneeId) return '';
    const member = members.find(m => m.id === assigneeId);
    return member ? `${member.first_name} ${member.last_name}` : '';
  }, [members]);

  // Filter bills based on search term - searches multiple fields
  const filteredBills = useMemo(() => {
    if (!searchTerm.trim()) return [];
    
    const term = searchTerm.toLowerCase();
    return bills
      .filter(bill => {
        // Search across multiple fields
        const billName = bill.bill_name?.toLowerCase() || '';
        const category = bill.category?.toLowerCase() || '';
        const notes = bill.notes?.toLowerCase() || '';
        const paymentAccountName = getPaymentAccountName(bill.payment_account_id).toLowerCase();
        const assigneeName = getAssigneeName(bill.assigned_to).toLowerCase();
        const amount = bill.amount?.toString() || '';
        const frequency = bill.billing_frequency?.toLowerCase() || '';
        
        return billName.includes(term) ||
               category.includes(term) ||
               notes.includes(term) ||
               paymentAccountName.includes(term) ||
               assigneeName.includes(term) ||
               amount.includes(term) ||
               frequency.includes(term);
      })
      .slice(0, 10); // Limit to 10 results
  }, [bills, searchTerm, getPaymentAccountName, getAssigneeName]);

  // Handle input change
  const handleChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(0);
  };

  // Handle clear
  const handleClear = () => {
    setSearchTerm('');
    setIsOpen(false);
    if (isMobileOverlayOpen && mobileInputRef.current) {
      mobileInputRef.current.focus();
    } else if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle bill selection
  const handleSelect = (bill) => {
    if (onBillSelect) {
      onBillSelect(bill);
    }
    setSearchTerm('');
    setIsOpen(false);
    setIsMobileOverlayOpen(false);
  };

  // Handle mobile search button click
  const handleMobileSearchClick = () => {
    setIsMobileOverlayOpen(true);
  };

  // Handle mobile overlay close
  const handleMobileClose = () => {
    setSearchTerm('');
    setIsOpen(false);
    setIsMobileOverlayOpen(false);
  };

  // Focus mobile input when overlay opens
  useEffect(() => {
    if (isMobileOverlayOpen && mobileInputRef.current) {
      // Small delay to ensure the overlay is rendered
      setTimeout(() => {
        mobileInputRef.current?.focus();
      }, 50);
    }
  }, [isMobileOverlayOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || filteredBills.length === 0) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        if (isMobileOverlayOpen) {
          handleMobileClose();
        } else {
          inputRef.current?.blur();
        }
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredBills.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredBills[highlightedIndex]) {
          handleSelect(filteredBills[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        if (isMobileOverlayOpen) {
          handleMobileClose();
        } else {
          inputRef.current?.blur();
        }
        break;
      default:
        break;
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const highlighted = dropdownRef.current.querySelector('.bill-search-item.highlighted');
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Close dropdown on click outside (desktop only)
  useEffect(() => {
    if (isMobileOverlayOpen) return; // Don't close on mobile overlay
    
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileOverlayOpen]);

  // Close dropdown on scroll outside (desktop only)
  useEffect(() => {
    if (!isOpen || isMobileOverlayOpen) return;
    
    const handleScroll = (e) => {
      // Don't close if scrolling inside the dropdown
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) {
        return;
      }
      setIsOpen(false);
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen, isMobileOverlayOpen]);

  // Prevent body scroll when mobile overlay is open
  useEffect(() => {
    if (isMobileOverlayOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOverlayOpen]);

  const hasValue = searchTerm.length > 0;
  const showDropdown = isOpen && hasValue;

  // Render search results list (shared between desktop and mobile)
  const renderResults = () => {
    if (!showDropdown) return null;

    return (
      <>
        {filteredBills.length === 0 ? (
          <div className="bill-search-empty">
            <span>No bills found</span>
          </div>
        ) : (
          filteredBills.map((bill, index) => {
            const categoryInfo = getCategoryInfo(bill.category);
            const { dueDate, isPaid, isOverdue } = bill.dueInfo || {};
            const dueDateDisplay = dueDate ? formatDateRelative(dueDate) : null;

            return (
              <div
                key={bill.id}
                className={`bill-search-item ${index === highlightedIndex ? 'highlighted' : ''} ${isPaid ? 'paid' : ''} ${isOverdue ? 'overdue' : ''} ${!bill.is_active ? 'inactive' : ''}`}
                onClick={() => handleSelect(bill)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <BillIcon 
                  iconUrl={bill.icon_url} 
                  categoryIcon={categoryInfo.icon} 
                  billName={bill.bill_name} 
                  size="sm" 
                />
                
                <div className="bill-search-item-info">
                  <div className="bill-search-item-name">
                    {bill.bill_name}
                    {bill.auto_pay && <Zap size={12} className="autopay-icon" />}
                  </div>
                  <div className="bill-search-item-meta">
                    <span className="bill-search-item-category">{bill.category || 'Uncategorized'}</span>
                    {dueDateDisplay && (
                      <>
                        <span className="meta-separator">•</span>
                        <span className={`bill-search-item-due ${isOverdue ? 'overdue' : ''}`}>
                          {isOverdue ? (
                            <><AlertCircle size={10} /> {dueDateDisplay}</>
                          ) : isPaid ? (
                            <><CheckCircle size={10} /> Paid</>
                          ) : (
                            <><Calendar size={10} /> {dueDateDisplay}</>
                          )}
                        </span>
                      </>
                    )}
                    {!bill.is_active && (
                      <>
                        <span className="meta-separator">•</span>
                        <span className="bill-search-item-inactive">
                          <EyeOff size={10} /> Inactive
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div className="bill-search-item-amount">
                  {formatAmount(bill.amount)}
                </div>
              </div>
            );
          })
        )}
      </>
    );
  };

  // Mobile search overlay (rendered via portal)
  const mobileOverlay = isMobileOverlayOpen ? ReactDOM.createPortal(
    <div className="bill-search-mobile-overlay">
      <div className="bill-search-mobile-header">
        <div className="bill-search-mobile-input-container">
          <Search size={18} className="bill-search-mobile-icon" />
          <input
            ref={mobileInputRef}
            type="text"
            value={searchTerm}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="bill-search-mobile-input"
            autoComplete="off"
          />
          {hasValue && (
            <button 
              type="button" 
              className="bill-search-mobile-clear" 
              onClick={handleClear}
            >
              <X size={18} />
            </button>
          )}
        </div>
        <button 
          type="button" 
          className="bill-search-mobile-cancel"
          onClick={handleMobileClose}
        >
          Cancel
        </button>
      </div>
      <div className="bill-search-mobile-results" ref={dropdownRef}>
        {renderResults()}
        {!hasValue && (
          <div className="bill-search-mobile-hint">
            <Search size={24} />
            <span>Search bills by name, category, notes, or amount</span>
          </div>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      {/* Desktop: Full search input */}
      <div 
        ref={containerRef}
        className={`custom-search bill-search bill-search-desktop ${disabled ? 'disabled' : ''} ${hasValue ? 'has-value' : ''} ${showDropdown ? 'dropdown-open' : ''} ${className}`}
      >
        <div className="custom-search-container">
          <Search size={18} className="custom-search-icon" />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleChange}
            onFocus={() => searchTerm && setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="custom-search-input"
            disabled={disabled}
            autoComplete="off"
          />
          {hasValue && (
            <button 
              type="button" 
              className="custom-search-clear" 
              onClick={handleClear}
              title="Clear search"
              disabled={disabled}
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Desktop Dropdown Results */}
        {showDropdown && !isMobileOverlayOpen && (
          <div className="bill-search-dropdown" ref={dropdownRef}>
            {renderResults()}
          </div>
        )}
      </div>

      {/* Mobile: Search icon button */}
      <button
        type="button"
        className={`bill-search-mobile-trigger ${disabled ? 'disabled' : ''}`}
        onClick={handleMobileSearchClick}
        disabled={disabled}
        title="Search bills"
      >
        <Search size={18} />
      </button>

      {/* Show Inactive Toggle */}
      {onShowInactiveChange && (
        <button
          type="button"
          className={`bill-search-inactive-toggle ${showInactive ? 'active' : ''}`}
          onClick={() => onShowInactiveChange(!showInactive)}
          title={showInactive ? 'Hide inactive bills' : 'Show inactive bills'}
        >
          {showInactive ? <Eye size={18} /> : <EyeOff size={18} />}
        </button>
      )}

      {/* Mobile overlay */}
      {mobileOverlay}
    </>
  );
}

export default BillSearchInput;
